import type { Language } from "../utilities/general";

/**
 * A single transcribed segment of a conversation.
 */
export interface AiConversationSegment {
  /** Speaker index (0-based, in chronological order). */
  index: number;
  /** Duration of this segment in milliseconds. */
  duration: number;
  /** Transcribed text for this segment. */
  text: string;
  /** BCP-47 locale detected for this segment, or "Undefined". */
  locale?: string;
  /** Diarised speaker number reported by Azure STT. */
  speakerNo: number;
}

/**
 * Result returned by the voice-to-text (Azure) conversation transcription API.
 * `/api/Translate/Voice/Azure` and `/api/Translate/Voice/Azure/QR`.
 */
export interface AiConversationTranscription {
  /** Total duration of the audio in milliseconds. */
  totalDuration: number;
  /** Full transcript with all segments joined together. */
  combinedText: string;
  /** Per-segment transcription, in chronological order. */
  texts: AiConversationSegment[];
  /** Distinct locales detected across all segments. */
  locales: string[];
}

/**
 * The `{ isSuccess, response }` envelope the backend wraps most AI endpoints in
 * (`GeneralResponse<T>`).
 */
export interface AiGeneralResponse<T> {
  isSuccess: boolean;
  response: T;
}

/**
 * The envelope the VmorAI Azure OpenAI client returns (`OpenAIResponse<T>`).
 * Note this sits *inside* {@link AiGeneralResponse}, so the raw payload is
 * double-nested: `{ isSuccess, response: { ..., response: T } }`.
 */
export interface AiOpenAIResponse<T> {
  id: string;
  /** Prompt context used, e.g. "Summarise and Translate". */
  context: string;
  /** The original text that was sent. */
  request: string;
  response: T;
  inputToken: number;
  outputToken: number;
  tokenUsage: number;
}

/** Target languages accepted by the translate APIs. */
export type AiTargetLang = "Chinese" | "English" | "Thai" | "Vietnamese";

/** A summary/translation returned by `/api/Summary/Supreme`. */
export interface AiSummaryAndTranslationItem {
  /** One of the application's BCP-47 language codes. */
  lang: Language;
  content: string;
}

/** Request body for `/api/Summary/Supreme` and `/api/Summary/Supreme/Token`. */
export interface AiMultiTargetLangRequest {
  targetLang: Language[];
  content: string;
}

/** Request body for /api/Translate and /api/Translate/Token. */
export interface AiTranslateRequest {
  targetLang: Language;
  content: string;
}

/** One translated variant returned by the translate API. */
export interface AiTranslationItem {
  /** Language of this variant, e.g. "Chinese" / "English". */
  lang: AiTargetLang | string;
  /** The translated text — this is the only field safe to send back to the API. */
  content: string;
}

/** @deprecated Kept for older call sites — prefer {@link AiOpenAIResponse}. */
export type AiTranslation = AiOpenAIResponse<AiTranslationItem[]>;

/**
 * Raw body of `/api/Translate` and `/api/Translate/Token`.
 *
 * NOTE: the innermost `response` is an ARRAY of variants, not a string. Never
 * put this object (or the array) into a request payload that expects plain
 * text — the store normalises it into {@link AiTranslateResult} first.
 */
export type AiTranslateRawResponse = AiGeneralResponse<AiTranslation>;

/**
 * What the user store hands back to components: the same `{ isSuccess, response }`
 * shape callers have always used, with `response` guaranteed to be plain text.
 */
export interface AiTranslateResult {
  isSuccess: boolean;
  /** Flattened translated text — safe to render or send onward. */
  response: string;
  /** Every language variant the API returned, if a caller needs more than one. */
  items: AiTranslationItem[];
  /** Untouched payload, for token usage / debugging. */
  raw: any;
}

/**
 * Flattens a translate API result down to a plain string.
 * Tolerates the new nested shape, the older shape (`{ response: "..." }`),
 * and a bare string.
 */
export const GetTranslatedText = (
  res: AiTranslateRawResponse | AiTranslation | { response?: string; } | string | null | undefined,
  lang?: AiTargetLang | string
): string => {
  if (!res) return "";
  if (typeof res === 'string') return res;
  const payload: any = (res as any).response ?? (res as any).content;
  if (!payload) return "";
  if (typeof payload === 'string') return payload;
  if (Array.isArray(payload)) {
    const matched = (lang ? payload.find((r: any) => r?.lang === lang) : undefined) ?? payload[0];
    return typeof matched === 'string' ? matched : (matched?.content ?? "");
  }
  return GetTranslatedText(payload, lang);
};

/** Pulls out every language variant, whatever nesting level it arrived at. */
export const GetTranslationItems = (res: any): AiTranslationItem[] => {
  if (!res || typeof res === 'string') return [];
  const payload: any = res.response ?? res.content;
  if (!payload) return [];
  if (Array.isArray(payload)) return payload.filter((i: any) => i && typeof i === 'object');
  return GetTranslationItems(payload);
};

/**
 * Normalises a raw translate payload so consumers can keep reading `res.response`
 * as a string, as they did before the response structure changed.
 */
export const NormalizeTranslation = (raw: any, lang?: AiTargetLang | string): AiTranslateResult => ({
  isSuccess: raw?.isSuccess ?? true,
  response: GetTranslatedText(raw, lang),
  items: GetTranslationItems(raw),
  raw,
});

/** Body of `/api/Summary` and `/api/Summary/Token` — `response` is plain text. */
export type AiSummaryResponse = AiGeneralResponse<string>;

/** What the store returns for `/api/Summary/Supreme`, already parsed. */
export interface AiSummarySupremeResult {
  isSuccess: boolean;
  /** The parsed summary/translation list. */
  response: AiSummaryAndTranslationItem[];
  raw: any;
}

const SUMMARY_LANGUAGE_MAP: Record<string, Language> = {
  "en": "en-AU" as Language,
  "en-au": "en-AU" as Language,
  "en-us": "en-AU" as Language,
  "english": "en-AU" as Language,
  "zh": "zh-CN" as Language,
  "zh-cn": "zh-CN" as Language,
  "chinese": "zh-CN" as Language,
  "th": "th-TH" as Language,
  "th-th": "th-TH" as Language,
  "thai": "th-TH" as Language,
  "vi": "vi-VN" as Language,
  "vi-vn": "vi-VN" as Language,
  "vietnamese": "vi-VN" as Language,
};

const parseSummaryPayload = (payload: any): any[] => {
  if (payload == null) return [];
  if (typeof payload === "string") {
    try {
      return parseSummaryPayload(JSON.parse(payload));
    } catch {
      return [];
    }
  }
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== "object") return [];

  if (Array.isArray(payload.translation)) return payload.translation;
  if (Array.isArray(payload.translations)) return payload.translations;
  if (Array.isArray(payload.items)) return payload.items;
  if (payload.response !== undefined) return parseSummaryPayload(payload.response);
  return [];
};

/**
 * Parses the Supreme response into the single shape used by STT consumers.
 * It accepts direct arrays as well as the usual `{ response: ... }` envelope,
 * including JSON encoded responses from older backend deployments.
 */
export const ParseSummaryAndTranslate = (raw: any): AiSummaryAndTranslationItem[] => {
  const candidates = parseSummaryPayload(raw?.response ?? raw?.data ?? raw);
  return candidates
    .map((item: any) => {
      const rawLang = String(item?.lang ?? item?.Lang ?? item?.language ?? "").trim().toLowerCase();
      const lang = SUMMARY_LANGUAGE_MAP[rawLang];
      const content = item?.content ?? item?.Content;
      return lang && typeof content === "string" ? { lang, content } : null;
    })
    .filter((item): item is AiSummaryAndTranslationItem => item !== null);
};

/** Herb / acupoint name set used by the herb translate endpoints. */
export interface AiHerbTranslation {
  nameEn?: string | null;
  nameCn?: string | null;
  pinYin?: string | null;
  latin?: string | null;
}
